import type { JSX } from 'react'
/**
 * Administration screens: users, roles and the org tree.
 *
 * Deliberately plain. These exist so the system is operable — assigning the
 * first role, placing people in branches, moving a department — not to be a
 * finished product.
 */

import { useCallback, useEffect, useState } from 'react'
import type { OrgUnit, ScopeType } from '@erp/contracts'
import { useAuth, useCan } from '../auth/auth-context'

interface Assignment {
  id: string
  roleId: string
  roleName: string
  scopeType: ScopeType
  orgUnitId: string | null
  orgUnitName: string | null
}

interface User {
  id: string
  name: string
  email: string | null
  upn: string | null
  authProvider: string
  primaryOrgUnitId: string | null
  primaryOrgUnitName: string | null
  isActive: boolean
  isBreakGlass: boolean
  lastLoginAt: string | null
  assignments: Assignment[]
}

interface Role {
  id: string
  name: string
  description: string | null
  isSystem: boolean
  permissions: string[]
  memberCount: number
}

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`/api${path}`, {
    ...init,
    headers: { 'content-type': 'application/json', ...(init?.headers ?? {}) },
    credentials: 'include',
  })
  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as { message?: string }
    throw new Error(body.message ?? `${response.status} ${response.statusText}`)
  }

  /**
   * Tolerate an empty body.
   *
   * Handlers that return `void` — granting a role, revoking one, setting an org
   * unit — produce 201 or 200 with NO body. Parsing that as JSON throws, and in
   * Safari the message is "The string did not match the expected pattern", which
   * looks like a validation failure rather than a successful write whose response
   * could not be read.
   */
  const text = await response.text()
  return (text === '' ? undefined : JSON.parse(text)) as T
}

function useAsync<T>(load: () => Promise<T>, deps: unknown[]): {
  data: T | null
  error: string | null
  reload: () => void
} {
  const [data, setData] = useState<T | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [tick, setTick] = useState(0)

  useEffect(() => {
    let cancelled = false
    load()
      .then((value) => {
        if (!cancelled) {
          setData(value)
          setError(null)
        }
      })
      .catch((cause: unknown) => {
        if (!cancelled) setError(cause instanceof Error ? cause.message : String(cause))
      })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick])

  return { data, error, reload: () => setTick((t) => t + 1) }
}

// ---------------------------------------------------------------------------

export function UsersPage(): JSX.Element {
  const { reload: reloadMe } = useAuth()
  const can = useCan()
  const users = useAsync(() => api<User[]>('/iam/users?includeInactive=true'), [])
  const roles = useAsync(() => api<Role[]>('/iam/roles'), [])
  const orgUnits = useAsync(() => api<OrgUnit[]>('/iam/org-units'), [])
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const act = useCallback(
    async (id: string, fn: () => Promise<unknown>) => {
      setBusy(id)
      setError(null)
      try {
        await fn()
        users.reload()
        // A change to your own roles must refresh your own menu too.
        await reloadMe()
      } catch (cause) {
        setError(cause instanceof Error ? cause.message : String(cause))
      } finally {
        setBusy(null)
      }
    },
    [users, reloadMe],
  )

  if (users.error !== null) return <ErrorBox message={users.error} />
  if (users.data === null) return <p className="erp-muted">Loading…</p>

  return (
    <section>
      <h1>Users</h1>
      <p className="erp-muted">
        Accounts are created by single sign-on, with no roles. Grant access here.
      </p>
      {error !== null && <div className="erp-error">{error}</div>}

      <table className="erp-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Org unit</th>
            <th>Roles</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {users.data.map((user) => (
            <tr key={user.id} className={user.isActive ? '' : 'erp-row-inactive'}>
              <td>
                <strong>{user.name}</strong>
                <div className="erp-muted erp-small">{user.upn ?? user.email}</div>
                {user.isBreakGlass && <span className="erp-tag">break-glass</span>}
                {!user.isActive && <span className="erp-tag">inactive</span>}
              </td>
              <td>
                {can('user.edit') && orgUnits.data !== null ? (
                  <select
                    value={user.primaryOrgUnitId ?? ''}
                    disabled={busy === user.id}
                    onChange={(e) =>
                      void act(user.id, () =>
                        api(`/iam/users/${user.id}/org-unit`, {
                          method: 'PATCH',
                          body: JSON.stringify({ orgUnitId: e.target.value || null }),
                        }),
                      )
                    }
                  >
                    <option value="">— none —</option>
                    {orgUnits.data.map((unit) => (
                      <option key={unit.id} value={unit.id}>
                        {' '.repeat(depthOf(unit) * 2)}
                        {unit.name}
                      </option>
                    ))}
                  </select>
                ) : (
                  (user.primaryOrgUnitName ?? '—')
                )}
              </td>
              <td>
                {user.assignments.length === 0 && <span className="erp-muted">none</span>}
                {user.assignments.map((assignment) => (
                  <div key={assignment.id} className="erp-assignment">
                    <span>
                      {assignment.roleName}
                      <span className="erp-muted erp-small">
                        {' '}
                        · {assignment.scopeType}
                        {assignment.orgUnitName !== null ? ` @ ${assignment.orgUnitName}` : ''}
                      </span>
                    </span>
                    {can('role.assign') && (
                      <button
                        type="button"
                        className="erp-link-button erp-small"
                        onClick={() =>
                          void act(user.id, () =>
                            api(`/iam/roles/assignments/${assignment.id}`, { method: 'DELETE' }),
                          )
                        }
                      >
                        revoke
                      </button>
                    )}
                  </div>
                ))}
              </td>
              <td>
                {can('role.assign') && roles.data !== null && orgUnits.data !== null && (
                  <AssignForm
                    roles={roles.data}
                    orgUnits={orgUnits.data}
                    disabled={busy === user.id}
                    onAssign={(payload) =>
                      void act(user.id, () =>
                        api('/iam/roles/assignments', {
                          method: 'POST',
                          body: JSON.stringify({ userId: user.id, ...payload }),
                        }),
                      )
                    }
                  />
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  )
}

function AssignForm({
  roles,
  orgUnits,
  disabled,
  onAssign,
}: {
  roles: Role[]
  orgUnits: OrgUnit[]
  disabled: boolean
  onAssign: (payload: { roleId: string; scopeType: ScopeType; orgUnitId: string | null }) => void
}): JSX.Element {
  const [roleId, setRoleId] = useState('')
  const [scope, setScope] = useState<ScopeType>('OWN')
  const [orgUnitId, setOrgUnitId] = useState('')

  const needsUnit = scope === 'ORG_UNIT' || scope === 'ORG_UNIT_TREE'

  return (
    <div className="erp-assign-form">
      <select value={roleId} onChange={(e) => setRoleId(e.target.value)} disabled={disabled}>
        <option value="">add role…</option>
        {roles.map((role) => (
          <option key={role.id} value={role.id}>
            {role.name}
          </option>
        ))}
      </select>

      {roleId !== '' && (
        <>
          <select
            value={scope}
            onChange={(e) => setScope(e.target.value as ScopeType)}
            disabled={disabled}
          >
            <option value="OWN">own records</option>
            <option value="ORG_UNIT">one org unit</option>
            <option value="ORG_UNIT_TREE">org unit + below</option>
            <option value="ALL">everything</option>
          </select>

          {needsUnit && (
            <select
              value={orgUnitId}
              onChange={(e) => setOrgUnitId(e.target.value)}
              disabled={disabled}
            >
              <option value="">choose unit…</option>
              {orgUnits.map((unit) => (
                <option key={unit.id} value={unit.id}>
                  {unit.name}
                </option>
              ))}
            </select>
          )}

          <button
            type="button"
            className="erp-button erp-small"
            disabled={disabled || (needsUnit && orgUnitId === '')}
            onClick={() => {
              onAssign({ roleId, scopeType: scope, orgUnitId: needsUnit ? orgUnitId : null })
              setRoleId('')
              setOrgUnitId('')
            }}
          >
            grant
          </button>
        </>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------

export function RolesPage(): JSX.Element {
  const roles = useAsync(() => api<Role[]>('/iam/roles'), [])

  if (roles.error !== null) return <ErrorBox message={roles.error} />
  if (roles.data === null) return <p className="erp-muted">Loading…</p>

  return (
    <section>
      <h1>Roles</h1>
      <p className="erp-muted">
        Roles are data and editable. Permissions are code — declared in the API and
        synced at boot, never editable here.
      </p>

      {roles.data.map((role) => (
        <article key={role.id} className="erp-card">
          <header>
            <h2>
              {role.name}
              {role.isSystem && <span className="erp-tag">system</span>}
            </h2>
            <span className="erp-muted erp-small">{role.memberCount} member(s)</span>
          </header>
          {role.description !== null && <p className="erp-muted">{role.description}</p>}
          <details>
            <summary>{role.permissions.length} permission(s)</summary>
            <ul className="erp-permission-list">
              {role.permissions.map((code) => (
                <li key={code}>
                  <code>{code}</code>
                </li>
              ))}
            </ul>
          </details>
        </article>
      ))}
    </section>
  )
}

// ---------------------------------------------------------------------------

export function OrgUnitsPage(): JSX.Element {
  const can = useCan()
  const units = useAsync(() => api<OrgUnit[]>('/iam/org-units'), [])
  const [error, setError] = useState<string | null>(null)

  if (units.error !== null) return <ErrorBox message={units.error} />
  if (units.data === null) return <p className="erp-muted">Loading…</p>

  async function move(id: string, parentId: string): Promise<void> {
    setError(null)
    try {
      // Show the blast radius first: a reparent can widen somebody's reach with
      // no permission changing.
      const impact = await api<{ affectedUsers: number; descendants: number }>(
        `/iam/org-units/${id}/move-impact`,
      )
      const ok = window.confirm(
        `Move this unit?\n\n` +
          `${impact.descendants} descendant unit(s) will move with it.\n` +
          `${impact.affectedUsers} user(s) will have their data scope recalculated.`,
      )
      if (!ok) return

      await api(`/iam/org-units/${id}/move`, {
        method: 'POST',
        body: JSON.stringify({ parentId }),
      })
      units.reload()
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause))
    }
  }

  return (
    <section>
      <h1>Organization</h1>
      <p className="erp-muted">
        Branch and department structure. Data scope is resolved against this tree.
      </p>
      {error !== null && <div className="erp-error">{error}</div>}

      <table className="erp-table">
        <thead>
          <tr>
            <th>Unit</th>
            <th>Type</th>
            <th>Path</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {units.data.map((unit) => (
            <tr key={unit.id} className={unit.isActive ? '' : 'erp-row-inactive'}>
              <td>
                <span style={{ paddingLeft: `${depthOf(unit) * 1.25}rem` }}>{unit.name}</span>
                <span className="erp-muted erp-small"> {unit.code}</span>
              </td>
              <td className="erp-muted erp-small">{unit.type}</td>
              <td>
                <code className="erp-small">{unit.path}</code>
              </td>
              <td>
                {can('org_unit.move') && unit.parentId !== null && (
                  <select
                    value=""
                    onChange={(e) => {
                      if (e.target.value !== '') void move(unit.id, e.target.value)
                    }}
                  >
                    <option value="">move under…</option>
                    {units.data
                      ?.filter((c) => c.id !== unit.id && !c.path.startsWith(unit.path))
                      .map((candidate) => (
                        <option key={candidate.id} value={candidate.id}>
                          {candidate.name}
                        </option>
                      ))}
                  </select>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  )
}

function depthOf(unit: OrgUnit): number {
  return Math.max(0, unit.path.split('/').filter(Boolean).length - 1)
}

function ErrorBox({ message }: { message: string }): JSX.Element {
  return <div className="erp-error">{message}</div>
}